import React, { useState, useEffect, Suspense } from 'react'
import { Routes, Route } from 'react-router-dom'
import AuthRouter from '../Global/AuthRouter'
import { ApiProvider } from '../Contexts/ApiContext'
import { Routes as AppRoutes, RouteList } from '../Global/Routes'
import ScrollToTop from '../Global/ScrollToTop'
import Analytics, { trackUserSignedIn } from '../Global/Analytics'
import IdleTimer from '../Components/auth/IdleTimerContainer'
import TimeoutModal from '../Components/auth/TimeoutModal'
import { SiteUnderMaintenancePage } from '../Components/SiteUnderMaintenance'
import UnsupportedBrowserPage from '../Components/UnsuportedBrowserPage'
import { datadogRum } from '@datadog/browser-rum'
import { useLocation } from 'react-router-dom'
import LoadingPage from '../Components/LoadingPage'
import NYDCA from '../Components/NYDCA'
import ForeclosureAvoidance from '../Components/ForeclosureAvoidance'
import HomeToolkit from '../Components/HomeToolkit/HomeToolkit'
import CommonApiProvider from '../Contexts/CommonApiContext'
import { useAuth0 } from '@auth0/auth0-react'
import { clearLocalCache } from '../services/cache'
import WiringInstructions from '../Components/WiringInstructions'

const ErrorBoundary = React.lazy(() =>
  import('../Containers/ErrorBoundary'),
)
const BadRequestPage = React.lazy(() =>
  import('../Components/BadRequestPage'),
)
const Layout = React.lazy(() => import('../Global/Layout'))
const LayoutModern = React.lazy(() =>
  import('../Global/LayoutModern'),
)
const CommonQuestions = React.lazy(() =>
  import('../Components/CommonQuestions'),
)
const CommonForms = React.lazy(() =>
  import('../Containers/CommonFormsContainer'),
)
const Contact = React.lazy(() => import('../Components/Contact'))
const PrivacyPolicy = React.lazy(() =>
  import('../Components/PrivacyPolicy'),
)
const TermsConditions = React.lazy(() =>
  import('../Components/TermsAndConditions'),
)

const AccessibilityStatement = React.lazy(() =>
  import('../Components/AccessibilityStatement'),
)
const ScheduleOfFees = React.lazy(() =>
  import('../Components/ScheduleOfFees'),
)
const Licenses = React.lazy(() => import('../Components/Licenses'))
const ImportantDisclosures = React.lazy(() =>
  import('../Components/ImportantDisclosures'),
)
const Claim = React.lazy(() => import('../Components/Claim'))
const HelpfulResources = React.lazy(() =>
  import('../Components/HelpfulResources'),
)
const Payment = React.lazy(() => import('../Components/Payment'))

const getResetTimerState = () => ({ isTimedOut: false, timer: null })

const getTimeUntilLogoutAlert = () =>
  process.env.REACT_APP_TIME_UNTIL_LOGOUT_ALERT || 840

const getTimeUntilLogoutAfterAlert = () =>
  process.env.REACT_APP_TIME_UNTIL_LOGOUT_AFTER_ALERT || 60

const defaultAuthenticated = { isAuthenticated: false, mfa: false }

const App = () => {
  const [, setIsAuthenticated] = useState(false)
  const [isAuthenticated1, setIsAuthenticated1] = useState({
    ...defaultAuthenticated,
  })

  const [, setUserInfo] = useState({})
  const [timer, setTimer] = useState(getResetTimerState())
  const [lastActive, setLastActive] = useState(null)

  const { pathname } = useLocation()
  const {
    isAuthenticated: auth0Authenticated,
    isLoading,
    user: authUser,
    logout: logOut,
  } = useAuth0()

  let userTraits = localStorage.getItem('ajs_user_traits')
  const redirectAllowed = localStorage.getItem('redirectAllowed')

  const modernRoutes = [
    '/',
    '/home-toolkit',
    '/common-questions',
    '/common-forms',
    '/confirm-wiring',
    '/helpful-resources',
    '/contact',
    '/payment',
  ]
  const IS_REGISTRATION_PAGE = pathname === '/register'

  const handleLogout = async () => {
    setLastActive(null)
    setTimer(getResetTimerState())
    logOut({
      logoutParams: {
        returnTo: window.location.origin,
      },
    })
  }

  useEffect(() => {
    if (!isLoading) {
      if (auth0Authenticated) {
        if (!!authUser) {
          setIsAuthenticated1({
            isAuthenticated: auth0Authenticated,
            mfa: authUser.mfa,
          })
          const signinSegment = localStorage.getItem('signSegment')
          if (!signinSegment) {
            localStorage.setItem('signSegment', 'true')
            setTimeout(() => {
              trackUserSignedIn()
            }, 2000)
          }
        } else {
          setIsAuthenticated1({ ...defaultAuthenticated })
          logOut({
            logoutParams: {
              returnTo: window.location.origin,
            },
          })
        }
      } else {
        setIsAuthenticated1({ ...defaultAuthenticated })
        clearLocalCache()
      }
    }
  }, [auth0Authenticated, isLoading, authUser, logOut])

  useEffect(() => {
    // Set user info and authentication status based on Auth0
    if (!isLoading && auth0Authenticated && authUser) {
      setUserInfo(authUser)
      setIsAuthenticated(true)
    } else {
      setUserInfo({})
      setIsAuthenticated(false)
      if (!userTraits) {
        localStorage.removeItem('ajs_user_traits')
      }
    }
  }, [auth0Authenticated, isLoading, authUser, userTraits])

  useEffect(() => {
    if (
      lastActive &&
      isAuthenticated1.isAuthenticated &&
      timer.isTimedOut
    ) {
      if (
        (new Date().getTime() - lastActive.getTime()) / 1000 >=
        parseInt(getTimeUntilLogoutAlert()) +
          parseInt(getTimeUntilLogoutAfterAlert())
      )
        handleLogout()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastActive, isAuthenticated1, timer.isTimedOut])

  useEffect(() => {
    // Run! Like go get some data from an API.
    datadogRum.init({
      applicationId: process.env.REACT_APP_DATADOG_RUM_APPLICATION_ID,
      clientToken: process.env.REACT_APP_DATADOG_RUM_CLIENT_TOKEN,
      site: process.env.REACT_APP_DATADOG_RUM_SERVICE,
      beforeSend: (event) => {
        if (event.error && event.error.message.includes('segment')) {
          return false
        }
      },
    })
  }, [])

  if (isLoading) {
    return <LoadingPage />
  }

  return (
    <div className="App">
      {isAuthenticated1.isAuthenticated ? (
        <>
          <IdleTimer
            timeInSeconds={getTimeUntilLogoutAlert()}
            onTimeout={(timer) => {
              setTimer({ timer, isTimedOut: true })

              // Idle timer keeps calling onTimeout as you are idle. We want to record the first time the user is idle and then reset the flag by setting it to null
              if (!lastActive)
                setLastActive(new Date(timer.getLastActiveTime()))
            }}
            onLogout={handleLogout}
          />
          {timer.isTimedOut ? (
            <TimeoutModal
              showModal={timer.isTimedOut}
              handleClose={() => {
                timer.timer.reset()
                setTimer({ ...timer, isTimedOut: false })
                setLastActive(null)
              }}
              handleLogout={() => {
                handleLogout()
              }}
              timeoutInSeconds={getTimeUntilLogoutAfterAlert()}
              lastActive={lastActive}
            />
          ) : (
            <div />
          )}
        </>
      ) : (
        <div />
      )}

      <UnsupportedBrowserPage>
        <ApiProvider
          isAuthenticated={isAuthenticated1.isAuthenticated}
        >
          <ScrollToTop />
          <Analytics
            userInfo={authUser}
            isAuthenticated={isAuthenticated1.isAuthenticated}
          />
          <Suspense
            fallback={
              <div>
                {' '}
                <LoadingPage isHomePage={pathname === '/'} />{' '}
              </div>
            }
          >
            {modernRoutes.includes(window.location.pathname) ? (
              <CommonApiProvider
                isAuthenticated={isAuthenticated1.isAuthenticated}
              >
                {!isLoading && (
                  <LayoutModern
                    isAuthenticated={isAuthenticated1.isAuthenticated}
                  >
                    <Routes>
                      {AppRoutes()}
                      <Route path="*" element={<BadRequestPage />} />
                    </Routes>
                  </LayoutModern>
                )}
              </CommonApiProvider>
            ) : (
              <CommonApiProvider
                isAuthenticated={isAuthenticated1.isAuthenticated}
              >
                {!isLoading && (
                  <Layout
                    isAuthenticated={isAuthenticated1.isAuthenticated}
                  >
                    <Routes>
                       {AppRoutes()}
                       <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                       <Route path="/terms-conditions" element={<TermsConditions />} />
                       <Route path="/schedule-of-borrower-fees" element={<ScheduleOfFees />} />
                       <Route path="/licenses" element={<Licenses />} />
                       <Route path="/important-disclosures" element={<ImportantDisclosures />} />
                       <Route path="/claims" element={<Claim />} />
                       <Route path="/accessibility-statement" element={<AccessibilityStatement />} />
                       <Route path="/nydca" element={<NYDCA />} />
                       <Route path="/foreclosure-avoidance" element={<ForeclosureAvoidance />} />
                       {/* Add additional routes as needed */}
                       <Route path="*" element={<BadRequestPage />} />
                    </Routes>
                  </Layout>
                )}
              </CommonApiProvider>
            )}
          </Suspense>
        </ApiProvider>
      </UnsupportedBrowserPage>
    </div>
  )
}

export default App
